import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';

import {
    ActionPreviewEdits,
    ActionPreviewEmailTemplateSection,
} from '../../../../../services/ai-poc/ai-poc.service';
import { RichTextEditorComponent } from '../../../../shared/rich-text-editor/rich-text-editor.component';
import { FlexSpacerDirective } from '../../../shared/directives/flex-spacer.directive';

/**
 * `email_template` section of an action-preview card.
 *
 * Two display modes:
 *  - **Read-only** (default): subject + body rendered statically in a
 *    soft gray card with the optional footnote underneath. The right side
 *    of the section header shows an `Edit` link when `section.editable`
 *    is true; clicking it switches the card to edit-mode.
 *  - **Edit**: subject input + `<app-rich-text-editor type="email">` body
 *    with Cancel / Save changes buttons. Save emits a local `(edit)`
 *    patch and returns to read-only; Cancel discards the form state.
 *
 * Edits never round-trip on their own — the host card stores them in
 * `pendingEdits` and posts the full delta on commit-action. This keeps
 * keystrokes local and predictable.
 *
 * The `categoryChip` field on the section was deliberately removed in
 * iteration 2 — status now lives as its own top-level `status_picker`
 * section so it can be reused by non-violation actions.
 */
@Component({
    selector: 'app-ap-email-template-section',
    templateUrl: './email-template-section.component.html',
    styleUrls: ['./email-template-section.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        FlexSpacerDirective,
        RichTextEditorComponent,
    ],
})
export class ActionPreviewEmailTemplateSectionComponent implements OnChanges {
    @Input({ required: true }) section!: ActionPreviewEmailTemplateSection;
    @Input() pendingEdit: ActionPreviewEdits['email_template'] | null = null;

    @Output() readonly edit = new EventEmitter<NonNullable<ActionPreviewEdits['email_template']>>();

    /** Whether the section is showing the edit form vs the read-only panel. */
    inEditMode = false;

    /**
     * RichTextEditor accepts a `FormGroup` + `controlName` OR a `FormControl`.
     * Using a FormGroup is more idiomatic across the codebase and survives
     * the editor's internal Angular forms wiring. Body is a `string` control
     * holding the HTML; subject is plain text.
     */
    readonly form = new FormGroup({
        subject: new FormControl<string>('', { nonNullable: true }),
        body: new FormControl<string>('', { nonNullable: true }),
    });

    constructor(private _cdr: ChangeDetectorRef) {}

    ngOnChanges(changes: SimpleChanges): void {
        // Seed the form whenever the canonical section payload OR the
        // host's pendingEdit changes — that way switching into edit-mode
        // always presents the most recent values (server preview merged
        // with any in-flight local edits).
        if (changes['section'] || changes['pendingEdit']) {
            this._resetFormToCurrent();
        }
    }

    get displaySubject(): string {
        return this.pendingEdit?.subject ?? this.section.subject;
    }

    get displayBody(): string {
        return this.pendingEdit?.body ?? this.section.body;
    }

    /**
     * Subject + body rendered with surface-area whitespace preserved. The
     * body comes from the LLM (or a user edit via RichTextEditor) and may
     * contain HTML; we render it via `[innerHTML]` in the template.
     */
    enterEditMode(): void {
        if (!this.section.editable) {
            return;
        }
        this._resetFormToCurrent();
        this.inEditMode = true;
        this._cdr.markForCheck();
    }

    cancelEdit(): void {
        this._resetFormToCurrent();
        this.inEditMode = false;
        this._cdr.markForCheck();
    }

    saveEdit(): void {
        const patch: NonNullable<ActionPreviewEdits['email_template']> = {
            subject: this.form.controls.subject.value,
            body: this.form.controls.body.value,
        };
        this.edit.emit(patch);
        this.inEditMode = false;
        this._cdr.markForCheck();
    }

    private _resetFormToCurrent(): void {
        this.form.setValue(
            {
                subject: this.displaySubject,
                body: this._asHtmlBody(this.displayBody),
            },
            { emitEvent: false },
        );
    }

    /**
     * The LLM produces the email body as plain text with `\n\n` paragraph
     * breaks. The read-only preview renders that with `white-space: pre-wrap`
     * (CSS) so the paragraph breaks are visible. CKEditor (TinyMCE) on the
     * other hand expects HTML — if you feed it raw `\n\n` it strips the
     * newlines and collapses everything to a single paragraph. Convert
     * once on the way in so the user sees the same shape they were just
     * looking at, then save plain HTML on the way out.
     *
     * If the body already looks like HTML (contains a block tag) we pass
     * it through unchanged — covers the case where the user has already
     * edited it once via the rich editor.
     */
    private _asHtmlBody(raw: string): string {
        if (raw === '' || raw === null || raw === undefined) {
            return '';
        }
        if (/<\s*(p|div|br|h[1-6]|ul|ol|li|blockquote)[\s>/]/i.test(raw)) {
            return raw;
        }
        // Split on blank lines (one or more empty lines = paragraph break),
        // wrap each paragraph in <p>. Single newlines inside a paragraph
        // become <br> so simple line breaks survive too.
        const paragraphs = raw.split(/\n{2,}/).map((para) => {
            const escaped = para
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;');
            return `<p>${escaped.replace(/\n/g, '<br>')}</p>`;
        });

        return paragraphs.join('');
    }
}
